import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, Navigation, X } from 'lucide-react';
import './RoutePlanner.scss';

const RoutePlanner = () => {
    const navigate = useNavigate();
    const [startLocation, setStartLocation] = useState({ name: '', lat: null, lon: null });
    const [endLocation, setEndLocation] = useState({ name: '', lat: null, lon: null });
    const [startSuggestions, setStartSuggestions] = useState([]);
    const [endSuggestions, setEndSuggestions] = useState([]);
    const [showStartSuggestions, setShowStartSuggestions] = useState(false);
    const [showEndSuggestions, setShowEndSuggestions] = useState(false);
    const [isLoadingStart, setIsLoadingStart] = useState(false);
    const [isLoadingEnd, setIsLoadingEnd] = useState(false);
    const [loadingRoute, setLoadingRoute] = useState(false);
    const [error, setError] = useState(null);
    
    const startRef = useRef(null);
    const endRef = useRef(null);
    const startSuggestionsRef = useRef(null);
    const endSuggestionsRef = useRef(null);
    const debounceTimerRef = useRef({ start: null, end: null });

    // Handle click outside to close suggestions
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                startSuggestionsRef.current &&
                !startSuggestionsRef.current.contains(event.target) &&
                startRef.current &&
                !startRef.current.contains(event.target)
            ) {
                setShowStartSuggestions(false);
            }
            if (
                endSuggestionsRef.current &&
                !endSuggestionsRef.current.contains(event.target) &&
                endRef.current &&
                !endRef.current.contains(event.target)
            ) {
                setShowEndSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const searchLocations = async (query, type) => {
        if (query.length < 2) {
            if (type === 'start') {
                setStartSuggestions([]);
                setShowStartSuggestions(false);
            } else {
                setEndSuggestions([]);
                setShowEndSuggestions(false);
            }
            return;
        }

        if (type === 'start') {
            setIsLoadingStart(true);
        } else {
            setIsLoadingEnd(true);
        }

        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query + ', Kerala, India')}&limit=5&addressdetails=1`,
                {
                    headers: {
                        'User-Agent': 'ChorullaPalli/1.0'
                    }
                }
            );

            if (response.ok) {
                const data = await response.json();
                
                const filtered = data
                    .filter(item => {
                        const displayName = item.display_name.toLowerCase();
                        return displayName.includes('kerala') || displayName.includes('india');
                    })
                    .map(item => {
                        const parts = item.display_name.split(',');
                        const locationName = parts[0].trim();
                        const location = parts.slice(1).join(',').trim();
                        
                        return {
                            description: item.display_name,
                            place_id: item.place_id || item.osm_id,
                            lat: parseFloat(item.lat),
                            lon: parseFloat(item.lon),
                            structured_formatting: {
                                main_text: locationName,
                                secondary_text: location
                            }
                        };
                    });

                if (type === 'start') {
                    setStartSuggestions(filtered);
                    setShowStartSuggestions(filtered.length > 0);
                } else {
                    setEndSuggestions(filtered);
                    setShowEndSuggestions(filtered.length > 0);
                }
            }
        } catch (error) {
            console.error('Error searching locations:', error);
        } finally {
            if (type === 'start') {
                setIsLoadingStart(false);
            } else {
                setIsLoadingEnd(false);
            }
        }
    };

    const handleStartInputChange = (e) => {
        const value = e.target.value;
        setStartLocation({ name: value, lat: null, lon: null });

        if (debounceTimerRef.current.start) {
            clearTimeout(debounceTimerRef.current.start);
        }

        debounceTimerRef.current.start = setTimeout(() => {
            if (value.length >= 2) {
                searchLocations(value, 'start');
            } else {
                setStartSuggestions([]);
                setShowStartSuggestions(false);
            }
        }, 300);
    };

    const handleEndInputChange = (e) => {
        const value = e.target.value;
        setEndLocation({ name: value, lat: null, lon: null });

        if (debounceTimerRef.current.end) {
            clearTimeout(debounceTimerRef.current.end);
        }

        debounceTimerRef.current.end = setTimeout(() => {
            if (value.length >= 2) {
                searchLocations(value, 'end');
            } else {
                setEndSuggestions([]);
                setShowEndSuggestions(false);
            }
        }, 300);
    };

    const handleStartSuggestionClick = (suggestion) => {
        const locationName = suggestion.structured_formatting?.main_text || 
                            suggestion.description.split(',')[0].trim();
        setStartLocation({
            name: locationName,
            lat: suggestion.lat,
            lon: suggestion.lon
        });
        setShowStartSuggestions(false);
        setStartSuggestions([]);
    };

    const handleEndSuggestionClick = (suggestion) => {
        const locationName = suggestion.structured_formatting?.main_text || 
                            suggestion.description.split(',')[0].trim();
        setEndLocation({
            name: locationName,
            lat: suggestion.lat,
            lon: suggestion.lon
        });
        setShowEndSuggestions(false);
        setEndSuggestions([]);
    };

    const clearStartLocation = () => {
        setStartLocation({ name: '', lat: null, lon: null });
    };

    const clearEndLocation = () => {
        setEndLocation({ name: '', lat: null, lon: null });
    };

    const calculateRoute = () => {
        if (!startLocation.lat || !startLocation.lon || !endLocation.lat || !endLocation.lon) {
            setError('Please select both start and end locations');
            return;
        }

        setError(null);

        // Navigate to route planner results page with URL parameters
        const params = new URLSearchParams({
            startName: startLocation.name,
            startLat: startLocation.lat.toString(),
            startLon: startLocation.lon.toString(),
            endName: endLocation.name,
            endLat: endLocation.lat.toString(),
            endLon: endLocation.lon.toString()
        });

        navigate(`/route-planner?${params.toString()}`);
    };


    return (
        <div className="route-planner">
            <div className="route-planner__form">
                <div className="route-planner__input-group" ref={startRef}>
                    <label className="route-planner__label">
                        <Navigation size={18} className="route-planner__label-icon" />
                        Start Location
                    </label>
                    <div className="route-planner__input-wrapper">
                        <span className="route-planner__prefix">from/</span>
                        <Search className="route-planner__input-icon" size={18} />
                        <input
                            type="text"
                            className="route-planner__input"
                            placeholder="enter start location..."
                            value={startLocation.name}
                            onChange={handleStartInputChange}
                            onFocus={() => {
                                if (startSuggestions.length > 0) {
                                    setShowStartSuggestions(true);
                                }
                            }}
                        />
                        {startLocation.name && (
                            <button
                                className="route-planner__clear-btn"
                                onClick={clearStartLocation}
                                title="Clear"
                            >
                                <X size={16} />
                            </button>
                        )}
                        {isLoadingStart && (
                            <div className="route-planner__loader">
                                <div className="spinner"></div>
                            </div>
                        )}
                    </div>
                    {showStartSuggestions && startSuggestions.length > 0 && (
                        <div className="route-planner__suggestions" ref={startSuggestionsRef}>
                            {startSuggestions.map((suggestion, index) => (
                                <div
                                    key={suggestion.place_id || index}
                                    className="route-planner__suggestion-item"
                                    onClick={() => handleStartSuggestionClick(suggestion)}
                                >
                                    <MapPin className="route-planner__suggestion-icon" size={16} />
                                    <div className="route-planner__suggestion-content">
                                        <div className="route-planner__suggestion-main">
                                            {suggestion.structured_formatting?.main_text || 
                                             suggestion.description.split(',')[0]}
                                        </div>
                                        <div className="route-planner__suggestion-secondary">
                                            {suggestion.structured_formatting?.secondary_text || 
                                             suggestion.description.split(',').slice(1).join(',').trim()}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="route-planner__input-group" ref={endRef}>
                    <label className="route-planner__label">
                        <MapPin size={18} className="route-planner__label-icon" />
                        End Location
                    </label>
                    <div className="route-planner__input-wrapper">
                        <span className="route-planner__prefix">to/</span>
                        <Search className="route-planner__input-icon" size={18} />
                        <input
                            type="text"
                            className="route-planner__input"
                            placeholder="enter end location..."
                            value={endLocation.name}
                            onChange={handleEndInputChange}
                            onFocus={() => {
                                if (endSuggestions.length > 0) {
                                    setShowEndSuggestions(true);
                                }
                            }}
                        />
                        {endLocation.name && (
                            <button
                                className="route-planner__clear-btn"
                                onClick={clearEndLocation}
                                title="Clear"
                            >
                                <X size={16} />
                            </button>
                        )}
                        {isLoadingEnd && (
                            <div className="route-planner__loader">
                                <div className="spinner"></div>
                            </div>
                        )}
                    </div>
                    {showEndSuggestions && endSuggestions.length > 0 && (
                        <div className="route-planner__suggestions" ref={endSuggestionsRef}>
                            {endSuggestions.map((suggestion, index) => (
                                <div
                                    key={suggestion.place_id || index}
                                    className="route-planner__suggestion-item"
                                    onClick={() => handleEndSuggestionClick(suggestion)}
                                >
                                    <MapPin className="route-planner__suggestion-icon" size={16} />
                                    <div className="route-planner__suggestion-content">
                                        <div className="route-planner__suggestion-main">
                                            {suggestion.structured_formatting?.main_text || 
                                             suggestion.description.split(',')[0]}
                                        </div>
                                        <div className="route-planner__suggestion-secondary">
                                            {suggestion.structured_formatting?.secondary_text || 
                                             suggestion.description.split(',').slice(1).join(',').trim()}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <button
                    className="route-planner__button"
                    onClick={calculateRoute}
                    disabled={!startLocation.lat || !endLocation.lat}
                >
                    <Navigation size={18} />
                    Plan Route
                </button>

                {error && (
                    <div className="route-planner__error">
                        {error}
                    </div>
                )}
            </div>
        </div>
    );
};

export default RoutePlanner;
